import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  CheckCircle2,
  Circle,
  Clock,
  Send,
  FileCheck,
  UserCheck,
  CalendarCheck,
  XCircle,
  Loader2,
  Mail,
  Building2,
  User,
  Calendar,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Workflow stages configuration
const WORKFLOW_STAGES = [
  {
    id: 'received',
    key: 'received',
    icon: Mail,
    statuses: ['received'],
    color: 'blue'
  },
  {
    id: 'response_sent',
    key: 'pending_insurer_confirmation',
    icon: Send,
    statuses: ['pending_insurer_confirmation'],
    color: 'purple'
  },
  {
    id: 'insurer_confirmed',
    key: 'confirmed_by_insurer',
    icon: FileCheck,
    statuses: ['confirmed_by_insurer'],
    color: 'indigo'
  },
  {
    id: 'specialist_assigned',
    key: 'specialist_assigned',
    icon: UserCheck,
    statuses: ['specialist_assigned'],
    color: 'cyan'
  },
  {
    id: 'scheduled',
    key: 'scheduled',
    icon: CalendarCheck,
    statuses: ['scheduled', 'in_progress', 'completed'],
    color: 'green'
  }
];

const statusLabels = {
  received: 'Recibida',
  pending_insurer_confirmation: 'Esperando Confirmación',
  confirmed_by_insurer: 'Confirmada por Aseguradora',
  specialist_assigned: 'Especialista Asignado',
  pending_confirmations: 'Pendiente Confirmaciones',
  pending_patient_confirmation: 'Esperando Paciente',
  pending_specialist_confirmation: 'Esperando Especialista',
  scheduled: 'Programada',
  in_progress: 'En Progreso',
  completed: 'Completada',
  rejected: 'Rechazada',
  cancelled: 'Cancelada'
};

const statusColors = {
  received: 'bg-blue-500',
  pending_insurer_confirmation: 'bg-purple-500',
  confirmed_by_insurer: 'bg-indigo-500',
  specialist_assigned: 'bg-cyan-500',
  scheduled: 'bg-green-500',
  in_progress: 'bg-orange-500',
  completed: 'bg-green-600',
  rejected: 'bg-red-500',
  cancelled: 'bg-gray-500'
};

export const InsuranceWorkflowPanel = ({ 
  order, 
  onOrderUpdated,
  specialists = []
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showSendResponseDialog, setShowSendResponseDialog] = useState(false);
  
  // Form states
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [insurerNotes, setInsurerNotes] = useState('');
  const [selectedSpecialist, setSelectedSpecialist] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTimeStart, setScheduledTimeStart] = useState('');
  const [scheduledTimeEnd, setScheduledTimeEnd] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [patientConfirmed, setPatientConfirmed] = useState(false);
  const [specialistConfirmed, setSpecialistConfirmed] = useState(false);
  const [insurerEmail, setInsurerEmail] = useState('');

  const token = localStorage.getItem('token');

  // Determine current stage
  const getCurrentStage = () => {
    const status = order?.status;
    if (status === 'rejected' || status === 'cancelled') return -1;
    
    for (let i = WORKFLOW_STAGES.length - 1; i >= 0; i--) {
      if (WORKFLOW_STAGES[i].statuses.includes(status)) {
        return i;
      }
    }
    return 0;
  };

  const currentStage = getCurrentStage();

  // Send response to insurer
  const handleSendResponse = async () => {
    if (!insurerEmail) {
      toast.error('Ingrese el email de la aseguradora');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/insurance/process-order`,
        {
          order_id: order.id,
          insurer_email: insurerEmail,
          validate_coverage: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        if (response.data.accepted) {
          toast.success('Respuesta de aceptación enviada a la aseguradora');
        } else {
          toast.warning('Orden rechazada. Se notificó a la aseguradora.');
        }
        setShowSendResponseDialog(false);
        if (onOrderUpdated) onOrderUpdated();
      }
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar respuesta');
    } finally {
      setLoading(false);
    }
  };

  // Confirm order from insurer
  const handleConfirmOrder = async () => {
    if (!confirmationNumber) {
      toast.error('Ingrese el número de confirmación');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/insurance/confirm-order`,
        {
          order_id: order.id,
          confirmation_number: confirmationNumber,
          insurer_notes: insurerNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Confirmación de aseguradora registrada');
      setShowConfirmDialog(false);
      setConfirmationNumber('');
      setInsurerNotes('');
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      console.error('Error confirming order:', error);
      toast.error(error.response?.data?.detail || 'Error al confirmar orden');
    } finally {
      setLoading(false);
    }
  };

  // Assign specialist
  const handleAssignSpecialist = async () => {
    if (!selectedSpecialist) {
      toast.error('Seleccione un especialista');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/insurance/assign-specialist`,
        {
          order_id: order.id,
          specialist_id: selectedSpecialist,
          scheduled_date: scheduledDate || null,
          scheduled_time_start: scheduledTimeStart || null,
          scheduled_time_end: scheduledTimeEnd || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Especialista asignado correctamente');
      setShowAssignDialog(false);
      setSelectedSpecialist('');
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      console.error('Error assigning specialist:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar especialista');
    } finally {
      setLoading(false);
    }
  };

  // Confirm appointment
  const handleConfirmAppointment = async () => {
    if (!scheduledDate || !scheduledTimeStart || !scheduledTimeEnd) {
      toast.error('Complete los datos de la cita');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/insurance/confirm-appointment`,
        {
          order_id: order.id,
          patient_confirmed: patientConfirmed,
          specialist_confirmed: specialistConfirmed,
          final_date: scheduledDate,
          final_time_start: scheduledTimeStart,
          final_time_end: scheduledTimeEnd,
          notes: appointmentNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Cita confirmada correctamente');
      setShowAppointmentDialog(false);
      if (onOrderUpdated) onOrderUpdated();
    } catch (error) {
      console.error('Error confirming appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al confirmar cita');
    } finally {
      setLoading(false);
    }
  };

  // Get next action based on current status
  const getNextAction = () => {
    switch (order?.status) {
      case 'received':
        return {
          label: 'Enviar Respuesta a Aseguradora',
          icon: Send,
          action: () => setShowSendResponseDialog(true),
          color: 'bg-purple-600 hover:bg-purple-700'
        };
      case 'pending_insurer_confirmation':
        return {
          label: 'Registrar Confirmación',
          icon: FileCheck,
          action: () => setShowConfirmDialog(true),
          color: 'bg-indigo-600 hover:bg-indigo-700'
        };
      case 'confirmed_by_insurer':
        return {
          label: 'Asignar Especialista',
          icon: UserCheck,
          action: () => setShowAssignDialog(true),
          color: 'bg-cyan-600 hover:bg-cyan-700'
        };
      case 'specialist_assigned':
        return {
          label: 'Confirmar Cita',
          icon: CalendarCheck,
          action: () => setShowAppointmentDialog(true),
          color: 'bg-green-600 hover:bg-green-700'
        };
      default:
        return null;
    }
  };

  const nextAction = getNextAction();

  if (order?.status === 'rejected' || order?.status === 'cancelled') {
    return (
      <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <XCircle className="h-5 w-5" />
            Orden {order.status === 'rejected' ? 'Rechazada' : 'Cancelada'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {order.rejection_reasons && (
            <div className="text-sm text-red-600 dark:text-red-400">
              <p className="font-medium mb-1">Motivos:</p>
              <ul className="list-disc list-inside">
                {order.rejection_reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Flujo de Autorización
            </span>
            <Badge className={`${statusColors[order?.status] || 'bg-gray-500'} text-white`}>
              {statusLabels[order?.status] || order?.status}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Workflow Timeline */}
          <div className="relative">
            <div className="flex items-center justify-between">
              {WORKFLOW_STAGES.map((stage, index) => {
                const StageIcon = stage.icon;
                const isCompleted = index < currentStage;
                const isCurrent = index === currentStage;
                const isPending = index > currentStage;
                
                return (
                  <React.Fragment key={stage.id}>
                    <div className="flex flex-col items-center relative z-10">
                      <div
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center
                          transition-all duration-300
                          ${isCompleted ? `bg-${stage.color}-500 text-white` : ''}
                          ${isCurrent ? `bg-${stage.color}-500 text-white ring-4 ring-${stage.color}-200 dark:ring-${stage.color}-800` : ''}
                          ${isPending ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500' : ''}
                        `}
                        style={isCompleted || isCurrent ? { backgroundColor: `var(--${stage.color}-500, #6366f1)` } : {}}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <StageIcon className="h-5 w-5" />
                        )}
                      </div>
                      <span className={`
                        text-xs mt-2 text-center max-w-[80px] leading-tight
                        ${isCurrent ? 'font-semibold text-primary' : ''}
                        ${isPending ? 'text-gray-400 dark:text-gray-500' : ''}
                      `}>
                        {stage.id === 'received' && 'Recibida'}
                        {stage.id === 'response_sent' && 'Respuesta Enviada'}
                        {stage.id === 'insurer_confirmed' && 'Confirmada'}
                        {stage.id === 'specialist_assigned' && 'Especialista'}
                        {stage.id === 'scheduled' && 'Programada'}
                      </span>
                    </div>
                    
                    {index < WORKFLOW_STAGES.length - 1 && (
                      <div className="flex-1 mx-2">
                        <div
                          className={`
                            h-1 rounded-full transition-all duration-300
                            ${index < currentStage ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}
                          `}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-xs text-muted-foreground">Aseguradora</p>
              <p className="font-medium">{order?.insurer || 'No especificada'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Autorización</p>
              <p className="font-medium">{order?.external_order_id || 'N/A'}</p>
            </div>
            {order?.insurer_confirmation_number && (
              <>
                <div>
                  <p className="text-xs text-muted-foreground">Confirmación Aseguradora</p>
                  <p className="font-medium text-green-600">{order.insurer_confirmation_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fecha Confirmación</p>
                  <p className="font-medium">
                    {order.insurer_confirmation_date ? 
                      new Date(order.insurer_confirmation_date).toLocaleDateString('es-CO') : 'N/A'}
                  </p>
                </div>
              </>
            )}
            {order?.assigned_specialist_name && (
              <div>
                <p className="text-xs text-muted-foreground">Especialista Asignado</p>
                <p className="font-medium text-cyan-600">{order.assigned_specialist_name}</p>
              </div>
            )}
            {order?.final_scheduled_date && (
              <div>
                <p className="text-xs text-muted-foreground">Cita Programada</p>
                <p className="font-medium text-green-600">
                  {order.final_scheduled_date} {order.final_scheduled_time_start}-{order.final_scheduled_time_end}
                </p>
              </div>
            )}
          </div>

          {/* Status History */}
          {order?.status_history && order.status_history.length > 0 && (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium mb-2">Historial</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {order.status_history.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <Clock className="h-3 w-3 mt-0.5 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground">
                        {new Date(entry.timestamp).toLocaleString('es-CO')}
                      </span>
                      <span className="mx-1">-</span>
                      <span>{entry.note}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Action Button */}
          {nextAction && (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                onClick={nextAction.action}
                className={`w-full ${nextAction.color} text-white`}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <nextAction.icon className="h-4 w-4 mr-2" />
                )}
                {nextAction.label}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Response Dialog */}
      <Dialog open={showSendResponseDialog} onOpenChange={setShowSendResponseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-500" />
              Enviar Respuesta a Aseguradora
            </DialogTitle>
            <DialogDescription>
              Se enviará un email de aceptación con los servicios y fechas programadas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="insurer-email">Email de la Aseguradora *</Label>
              <Input
                id="insurer-email"
                type="email"
                placeholder="autorizaciones@aseguradora.com"
                value={insurerEmail}
                onChange={(e) => setInsurerEmail(e.target.value)}
              />
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
              <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                Se incluirá en el email:
              </p>
              <ul className="text-blue-600 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Datos del paciente</li>
                <li>Servicios autorizados con fechas</li>
                <li>Medicamentos (si aplica)</li>
                <li>Instrucciones para confirmar</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendResponseDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendResponse} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Enviar Respuesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Order Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-indigo-500" />
              Registrar Confirmación de Aseguradora
            </DialogTitle>
            <DialogDescription>
              Ingrese el número de confirmación proporcionado por la aseguradora
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirmation-number">Número de Confirmación *</Label>
              <Input
                id="confirmation-number"
                placeholder="Ej: SURA-CONF-2026-123456"
                value={confirmationNumber}
                onChange={(e) => setConfirmationNumber(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="insurer-notes">Notas de la Aseguradora (opcional)</Label>
              <Textarea
                id="insurer-notes"
                placeholder="Notas adicionales..."
                value={insurerNotes}
                onChange={(e) => setInsurerNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmOrder} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileCheck className="h-4 w-4 mr-2" />}
              Registrar Confirmación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Specialist Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-cyan-500" />
              Asignar Especialista
            </DialogTitle>
            <DialogDescription>
              Seleccione el especialista que atenderá esta orden
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Especialista *</Label>
              <Select value={selectedSpecialist} onValueChange={setSelectedSpecialist}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar especialista" />
                </SelectTrigger>
                <SelectContent>
                  {specialists.map((spec) => (
                    <SelectItem key={spec.id} value={spec.id}>
                      {spec.first_name} {spec.last_name} - {spec.specialty || spec.type_name || 'General'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Inicio</Label>
                <Input
                  type="time"
                  value={scheduledTimeStart}
                  onChange={(e) => setScheduledTimeStart(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Fin</Label>
                <Input
                  type="time"
                  value={scheduledTimeEnd}
                  onChange={(e) => setScheduledTimeEnd(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAssignSpecialist} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />}
              Asignar Especialista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-green-500" />
              Confirmar Cita Final
            </DialogTitle>
            <DialogDescription>
              Confirme los datos de la cita y las confirmaciones del paciente y especialista
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Fecha de Cita *</Label>
                <Input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Inicio *</Label>
                <Input
                  type="time"
                  value={scheduledTimeStart}
                  onChange={(e) => setScheduledTimeStart(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hora Fin *</Label>
                <Input
                  type="time"
                  value={scheduledTimeEnd}
                  onChange={(e) => setScheduledTimeEnd(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex gap-4 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={patientConfirmed}
                  onChange={(e) => setPatientConfirmed(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  Paciente Confirmado
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={specialistConfirmed}
                  onChange={(e) => setSpecialistConfirmed(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="flex items-center gap-1">
                  <UserCheck className="h-4 w-4" />
                  Especialista Confirmado
                </span>
              </label>
            </div>
            
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                placeholder="Notas adicionales sobre la cita..."
                value={appointmentNotes}
                onChange={(e) => setAppointmentNotes(e.target.value)}
              />
            </div>
            
            {(!patientConfirmed || !specialistConfirmed) && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  La cita quedará en estado pendiente hasta que ambas partes confirmen.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppointmentDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmAppointment} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarCheck className="h-4 w-4 mr-2" />}
              Confirmar Cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InsuranceWorkflowPanel;
